import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type TooltipPosition = "top" | "right" | "bottom" | "left";

interface TooltipProps {
  label: string;
  title?: ReactNode;
  children: ReactNode;
  offset?: number;
  pos?: TooltipPosition;
  arrow?: boolean;
  className?: string;
}

type Coords = { top: number; left: number; transform: string };

const ARROW_CLASS: Record<TooltipPosition, string> = {
  top: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2",
  bottom: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2",
  left: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2",
  right: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2",
};

export const Tooltip = ({ label, children, title, pos = "bottom", offset = 10, arrow, className }: TooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  // Positioned via a portal + `position: fixed` so the tooltip escapes the
  // overflow/clipping of ancestors (e.g. the scrollable table panel).
  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const byPosition: Record<TooltipPosition, Coords> = {
      top: { top: rect.top - offset, left: rect.left + rect.width / 2, transform: "translate(-50%, -100%)" },
      bottom: { top: rect.bottom + offset, left: rect.left + rect.width / 2, transform: "translate(-50%, 0)" },
      left: { top: rect.top + rect.height / 2, left: rect.left - offset, transform: "translate(-100%, -50%)" },
      right: { top: rect.top + rect.height / 2, left: rect.right + offset, transform: "translate(0, -50%)" },
    };
    setCoords(byPosition[pos]);
  };

  return (
    <div ref={triggerRef} className="inline-block" onMouseEnter={show} onMouseLeave={() => setCoords(null)}>
      {children}
      {coords &&
        createPortal(
          <div
            style={{ position: "fixed", top: coords.top, left: coords.left, transform: coords.transform }}
            className={cn(
              "z-50 w-max bg-gray-800 text-white text-sm rounded-md shadow-lg pointer-events-none",
              title ? "p-4" : "px-3 py-2",
              className,
            )}
          >
            {arrow && <span className={cn("absolute size-2 rotate-45 bg-gray-800", ARROW_CLASS[pos])} />}
            {title && <p className="text-lg mb-1">{title}</p>}
            <div className="text-sm">{label}</div>
          </div>,
          document.body,
        )}
    </div>
  );
};
